import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

import { useDeleteEquipmentMutation, useEquipmentDetailsQuery, useUserDetailsQuery } from 'api';
import { useRegisterRequestedEquipmentMutation } from 'api/mutations/useRegisterRequestedEquipmentMutation';
import { GoBackButtonWithTitle, NewConfirmDialog, Spinner } from 'components';
import { EquipmentDetails } from 'features/equipments/components/EquipmentDetails';
import { useAuth, useModal } from 'hooks';
import { Links } from 'settings';
import { EquipmentStatus } from 'types';

import { PageW } from './styled';

export function EquipmentDetailsPage(): ReactElement {
  const { equipmentId = '', employeeId = '' } = useParams<string>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isManager, user } = useAuth();

  const { data: equipment, isLoading: isEquipmentLoading } = useEquipmentDetailsQuery({
    userId: employeeId,
    equipmentId,
  });

  const { user: assignedUser, isLoading: isUserLoading } = useUserDetailsQuery({ employeeId });

  const {
    isOpen: isDeleteOpen,
    handleOpenModal: openDeleteModal,
    handleCloseModal: closeDeleteModal,
  } = useModal();

  const { deleteEquipmentMutation, isLoading: isDeleteLoading } = useDeleteEquipmentMutation({
    onSuccessHandler: () => {
      closeDeleteModal();
      navigate(-1);
    },
  });

  const { registerRequestedEquipmentMutation } = useRegisterRequestedEquipmentMutation({
    invalidateQueries: ['equipments'],
    successText: t('toasts.equipments.success.register'),
  });

  const handleDelete = () => {
    deleteEquipmentMutation({ userId: employeeId, equipmentId });
  };

  const handleConfirm = () => {
    registerRequestedEquipmentMutation({ userId: employeeId, equipmentId });
  };

  const handleEdit = () => {
    navigate(
      generatePath(Links.profile.equipmentList.equipment.equipmentId.edit, {
        equipmentId,
        employeeId,
      }),
    );
  };

  if (isEquipmentLoading || isUserLoading) {
    return <Spinner />;
  }

  if (!equipment) {
    return <PageW />;
  }

  const isCurrentAuthor = user?.id === equipment.author_id;
  const isCurrentAssignee = user?.id === equipment.user_id;
  const isEditableByManager =
    isManager &&
    ((isCurrentAuthor && isCurrentAssignee && equipment.is_editable) ||
      (isCurrentAuthor && !isCurrentAssignee) ||
      (!isCurrentAuthor && !isCurrentAssignee));
  const canEdit = equipment.is_editable || isEditableByManager;
  const canDelete = canEdit;
  const isRequested = equipment.status === EquipmentStatus.REQUESTED;
  const isConfirmDisabled =
    !isCurrentAssignee && isManager && user?.id !== equipment.assigned_to_id;
  const canConfirm = isRequested && !isConfirmDisabled;

  return (
    <PageW>
      <GoBackButtonWithTitle
        title={t('common.back')}
        onClick={() => navigate(-1)}
        containerSx={{ marginTop: 0 }}
      />
      <EquipmentDetails
        equipment={equipment}
        assignedUser={assignedUser}
        canEdit={canEdit}
        canDelete={canDelete}
        canConfirm={canConfirm}
        onEdit={handleEdit}
        onDelete={openDeleteModal}
        onConfirm={handleConfirm}
      />
      <NewConfirmDialog
        title={t('equipments.removeModal.title', { equipmentName: equipment.name })}
        subTitle={t('equipments.removeModal.subtitle')}
        open={isDeleteOpen}
        onConfirm={handleDelete}
        onClose={closeDeleteModal}
        isLoading={isDeleteLoading}
        submitLabel={t('equipments.removeModal.successButton')}
        cancelLabel={t('equipments.removeModal.cancelButton')}
      />
    </PageW>
  );
}
